import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Activity, RefreshCw, TrendingUp } from "lucide-react";
import api from "@/lib/api";

const COINS = [
  { k: "btc", label: "BTC/USDT", sym: "BTC/USDT" },
  { k: "eth", label: "ETH/USDT", sym: "ETH/USDT" },
  { k: "gold", label: "XAU/USD", sym: "XAU/USD" },
];
const TFS = [{ d: 1, label: "1D" }, { d: 7, label: "7D" }, { d: 30, label: "30D" }];

function CandleChart({ candles }) {
  if (!candles?.length)
    return <div className="grid h-[320px] place-items-center text-sm text-white/40">No chart data available</div>;
  const W = 900, H = 320, padL = 8, padR = 62, padY = 16;
  const hi = Math.max(...candles.map((c) => c.h));
  const lo = Math.min(...candles.map((c) => c.l));
  const span = hi - lo || 1;
  const cw = (W - padL - padR) / candles.length;
  const x = (i) => padL + i * cw + cw / 2;
  const y = (v) => padY + (H - padY * 2) * (1 - (v - lo) / span);
  const last = candles[candles.length - 1];
  const lastUp = last.c >= candles[0].c;
  const lineCol = lastUp ? "#22c55e" : "#ef4444";
  const grid = 5;
  const dp = hi < 10 ? 4 : 2;
  const closePts = candles.map((c, i) => `${x(i)},${y(c.c)}`).join(" ");
  const areaPts = `${padL},${H - padY} ${closePts} ${x(candles.length - 1)},${H - padY}`;
  const lx = x(candles.length - 1), ly = y(last.c);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="none">
      <defs>
        <linearGradient id="areaGlow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineCol} stopOpacity="0.42" />
          <stop offset="55%" stopColor={lineCol} stopOpacity="0.12" />
          <stop offset="100%" stopColor={lineCol} stopOpacity="0" />
        </linearGradient>
        <filter id="lineBlur" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2.2" />
        </filter>
      </defs>

      {Array.from({ length: grid + 1 }).map((_, i) => {
        const gy = padY + ((H - padY * 2) / grid) * i;
        const val = hi - (span / grid) * i;
        return (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={gy} y2={gy} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            <text x={W - padR + 6} y={gy + 3} fill="rgba(255,255,255,0.45)" fontSize="10" fontFamily="monospace">
              {val.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })}
            </text>
          </g>
        );
      })}

      {/* day high / low reference lines */}
      <line x1={padL} x2={W - padR} y1={y(hi)} y2={y(hi)} stroke="#22c55e" strokeWidth="1" strokeDasharray="2 5" opacity="0.45" />
      <text x={padL + 2} y={y(hi) + 10} fill="#22c55e" fontSize="9" fontFamily="monospace">H {hi.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })}</text>
      <line x1={padL} x2={W - padR} y1={y(lo)} y2={y(lo)} stroke="#ef4444" strokeWidth="1" strokeDasharray="2 5" opacity="0.45" />
      <text x={padL + 2} y={y(lo) - 4} fill="#ef4444" fontSize="9" fontFamily="monospace">L {lo.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })}</text>

      {/* sparkle area fill under close-price */}
      <polygon points={areaPts} fill="url(#areaGlow)" />
      <polyline points={closePts} fill="none" stroke={lineCol} strokeWidth="1.4" opacity="0.55" filter="url(#lineBlur)" />
      <polyline points={closePts} fill="none" stroke={lineCol} strokeWidth="1.4" opacity="0.85" />

      {candles.map((c, i) => {
        const cx = x(i);
        const up = c.c >= c.o;
        const col = up ? "#22c55e" : "#ef4444";
        const bt = y(Math.max(c.o, c.c));
        const bb = y(Math.min(c.o, c.c));
        return (
          <g key={i}>
            <line x1={cx} x2={cx} y1={y(c.h)} y2={y(c.l)} stroke={col} strokeWidth="1" opacity="0.9" />
            <rect x={cx - Math.max(1.5, cw * 0.32)} y={bt} width={Math.max(2, cw * 0.64)} height={Math.max(1, bb - bt)} fill={col} rx="0.5" />
          </g>
        );
      })}

      {/* live last-price line + pulsing dot */}
      <line x1={padL} x2={W - padR} y1={ly} y2={ly} stroke={lineCol} strokeWidth="1" strokeDasharray="4 4" opacity="0.7" />
      <circle cx={lx} cy={ly} r="6" fill={lineCol} opacity="0.25">
        <animate attributeName="r" values="4;12;4" dur="1.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.35;0;0.35" dur="1.6s" repeatCount="indefinite" />
      </circle>
      <circle cx={lx} cy={ly} r="3" fill={lineCol} />
    </svg>
  );
}

export default function LiveMarketChart({ title = "Live Markets" }) {
  const [prices, setPrices] = useState(null);
  const [pLoading, setPLoading] = useState(false);
  const [coin, setCoin] = useState("btc");
  const [tf, setTf] = useState(1);
  const [ohlc, setOhlc] = useState(null);
  const [oLoading, setOLoading] = useState(false);
  const [flash, setFlash] = useState(null); // "up" | "down" — flashes on live tick
  const timer = useRef();
  const prevPrice = useRef({});

  const loadPrices = useCallback(async () => {
    setPLoading(true);
    try {
      const r = await api.get("/market/prices");
      setPrices(r.data);
      (r.data.items || []).forEach((it) => {
        const p = prevPrice.current[it.symbol];
        if (p != null && it.price !== p) { setFlash(it.price > p ? "up" : "down"); setTimeout(() => setFlash(null), 600); }
        prevPrice.current[it.symbol] = it.price;
      });
    } catch { setPrices({ items: [], available: false }); }
    finally { setPLoading(false); }
  }, []);
  const loadOhlc = useCallback(async (c, d) => {
    setOLoading(true);
    try { const r = await api.get(`/market/ohlc/${c}?days=${d}`); setOhlc(r.data); }
    catch { setOhlc({ candles: [], available: false }); }
    finally { setOLoading(false); }
  }, []);

  useEffect(() => {
    loadPrices();
    timer.current = setInterval(loadPrices, 8000); // fast live polling
    return () => clearInterval(timer.current);
  }, [loadPrices]);
  useEffect(() => { loadOhlc(coin, tf); }, [coin, tf, loadOhlc]);
  // refresh candles periodically so the historical base stays fresh
  useEffect(() => {
    const id = setInterval(() => loadOhlc(coin, tf), 45000);
    return () => clearInterval(id);
  }, [coin, tf, loadOhlc]);

  const active = COINS.find((c) => c.k === coin);
  const livePrice = prices?.items?.find((i) => i.symbol === active?.sym)?.price;
  const liveChange = prices?.items?.find((i) => i.symbol === active?.sym)?.change;

  // fold the current live price into the last candle so the chart visibly moves
  const liveCandles = useMemo(() => {
    const base = ohlc?.candles;
    if (!base?.length || livePrice == null) return base;
    const out = base.map((c) => ({ ...c }));
    const l = out[out.length - 1];
    l.c = livePrice;
    l.h = Math.max(l.h, livePrice);
    l.l = Math.min(l.l, livePrice);
    return out;
  }, [ohlc, livePrice]);

  const dpFor = (s) => (["EUR/USD", "GBP/USD"].includes(s) ? 4 : 2);

  return (
    <div className="overflow-hidden rounded-3xl" data-testid="live-market-chart"
      style={{ background: "linear-gradient(160deg,#0f1613,#070b09)", border: "1px solid rgba(34,197,94,0.30)", boxShadow: "0 24px 60px -24px rgba(0,0,0,0.7)" }}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-500/15 px-5 py-4">
        <div className="flex items-center gap-2.5">
          <div className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: "rgba(34,197,94,0.16)" }}>
            <TrendingUp size={18} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="font-display text-base font-bold leading-tight text-white">{title}</h3>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-300" data-testid="live-market-status">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <Activity size={11} /> LIVE · MARKET MONITORING ACTIVE
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {livePrice != null && (
            <div className={`rounded-xl px-3 py-1.5 text-right transition-colors duration-300 ${flash === "up" ? "bg-emerald-500/20" : flash === "down" ? "bg-rose-500/20" : "bg-white/5"}`} data-testid="chart-live-price">
              <p className="font-mono text-sm font-bold text-white">{Number(livePrice).toLocaleString("en-US", { minimumFractionDigits: dpFor(active?.sym), maximumFractionDigits: dpFor(active?.sym) })}</p>
              {liveChange != null && <p className={`text-[10px] font-bold ${liveChange >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{liveChange >= 0 ? "▲" : "▼"} {Math.abs(liveChange).toFixed(2)}% today</p>}
            </div>
          )}
          <span className="flex items-center gap-2 text-[11px] text-white/50">
            <button onClick={loadPrices} title="Refresh" className="hover:text-white"><RefreshCw size={13} className={pLoading ? "animate-spin" : ""} /></button>
            {prices?.updated_at ? new Date(prices.updated_at).toLocaleTimeString() : "—"}
          </span>
        </div>
      </div>

      {/* Ticker row */}
      <div className="grid grid-cols-2 gap-px bg-emerald-500/10 sm:grid-cols-3 md:grid-cols-5">
        {(prices?.items || (pLoading ? Array.from({ length: 5 }) : [])).map((t, i) => {
          if (!t) return <div key={i} className="h-16 animate-pulse bg-white/[0.03]" />;
          const dp = dpFor(t.symbol);
          return (
            <div key={t.symbol} className="bg-[#0b110e] px-4 py-2.5" data-testid={`market-tick-${t.symbol}`}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-white/50">{t.symbol}</p>
              <p className="font-mono text-sm font-bold text-white">{Number(t.price).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })}</p>
              <p className={`text-[11px] font-semibold ${t.change == null ? "text-white/40" : t.change >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {t.change == null ? "—" : `${t.change >= 0 ? "▲" : "▼"} ${Math.abs(t.change).toFixed(2)}%`}
              </p>
            </div>
          );
        })}
      </div>

      {/* Chart controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-4">
        <div className="flex gap-1.5">
          {COINS.map((c) => (
            <button key={c.k} onClick={() => setCoin(c.k)}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${coin === c.k ? "bg-emerald-500 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
              data-testid={`chart-coin-${c.k}`}>{c.label}</button>
          ))}
        </div>
        <div className="flex gap-1.5">
          {TFS.map((t) => (
            <button key={t.d} onClick={() => setTf(t.d)}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${tf === t.d ? "bg-emerald-500 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
              data-testid={`chart-tf-${t.label}`}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="px-3 pb-4 pt-3">
        <div className="h-[320px] overflow-hidden rounded-2xl" style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.06)" }}>
          {oLoading && !ohlc ? (
            <div className="grid h-full place-items-center text-white/50"><RefreshCw size={20} className="animate-spin" /></div>
          ) : ohlc && !ohlc.available ? (
            <div className="grid h-full place-items-center text-sm text-amber-300">Chart data temporarily unavailable</div>
          ) : (
            <CandleChart candles={liveCandles} />
          )}
        </div>
        <p className="mt-2 px-2 text-center text-[11px] text-white/40">{active?.label} · Live price updates every 8s · monitoring only — not a live broker/exchange trade execution.</p>
      </div>
    </div>
  );
}
